import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { api, fmt, fmtDec, EGG_ORDER, DEFECT_ORDER, MULTIPLIERS, BREED_LABELS } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Boxes, TrendingUp, Package, ShieldCheck, AlertTriangle, Clock, Egg, CalendarDays, ChevronDown, Info, RotateCcw } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

export default function Stok() {
  return (
    <div>
      <PageHeader title="Stok" subtitle="Anlık net depo bakiyesi ve ileriye dönük stok projeksiyonu" />
      <Tabs defaultValue="current" className="w-full">
        <TabsList className="mb-4" data-testid="stok-tabs">
          <TabsTrigger value="current" data-testid="stok-tab-current"><Boxes size={15} className="mr-1.5" /> Güncel Stok</TabsTrigger>
          <TabsTrigger value="daily" data-testid="stok-tab-daily"><Egg size={15} className="mr-1.5" /> Günlük Çıkan Üretim</TabsTrigger>
          <TabsTrigger value="projected" data-testid="stok-tab-projected"><TrendingUp size={15} className="mr-1.5" /> Öngörülen Stok</TabsTrigger>
        </TabsList>
        <TabsContent value="current"><CurrentStockView /></TabsContent>
        <TabsContent value="daily"><DailyProductionSummaryView /></TabsContent>
        <TabsContent value="projected"><ProjectedStockView /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Güncel Stok (anlık net depo) ---------------- */
function CurrentStockView() {
  const { meta, coops } = useApp();
  const { can } = useAuth();
  const locations = coops.filter((c) => (c.type === "location") || (c.type === "warehouse" && can("view_central_warehouse_stock")));
  const breeds = meta.breeds || [];
  const items = [...meta.egg_sizes, ...meta.defects];
  const [fc, setFc] = useState([]);
  const [breed, setBreed] = useState("all");
  const [rows, setRows] = useState([]);
  const TODAY = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const [targetDate, setTargetDate] = useState(TODAY);
  const isHistorical = targetDate < TODAY;

  const load = useCallback(async () => {
    const params = [];
    if (breed !== "all") params.push(`breed=${breed}`);
    if (targetDate && targetDate < TODAY) params.push(`as_of=${targetDate}`);
    const { data } = await api.get(`/stock${params.length ? `?${params.join("&")}` : ""}`);
    let out = data.filter((d) => Object.values(d.sizes || {}).some((v) => v !== 0));
    if (fc.length) out = out.filter((d) => fc.includes(d.coop_id));
    setRows(out);
  }, [fc, breed, targetDate]);
  useEffect(() => { load(); }, [load]);

  const sum = (keys, r) => keys.reduce((a, k) => a + (r.sizes?.[k] || 0), 0);
  const totalCartons = rows.reduce((a, r) => a + sum(EGG_ORDER.concat(DEFECT_ORDER), r), 0);
  const commercial = rows.reduce((a, r) => a + sum(EGG_ORDER, r), 0);
  const fire = rows.reduce((a, r) => a + sum(DEFECT_ORDER, r), 0);
  const lastUpdate = rows.map((r) => r.updated_at).filter(Boolean).sort().slice(-1)[0];
  const grand = {};
  items.forEach((s) => { grand[s.key] = rows.reduce((a, r) => a + (r.sizes?.[s.key] || 0), 0); });

  // group: location -> breeds, with per-breed and per-location subtotals
  const allKeys = EGG_ORDER.concat(DEFECT_ORDER);
  const byLoc = {};
  rows.forEach((r) => { (byLoc[r.coop_id] || (byLoc[r.coop_id] = { name: r.location_name, breeds: [] })).breeds.push(r); });
  const breedRank = { kirmizi: 0, tintin: 1, beyaz: 2 };
  const groups = Object.values(byLoc).sort((a, b) => a.name.localeCompare(b.name, "tr"));
  groups.forEach((g) => g.breeds.sort((a, b) => (breedRank[a.breed] ?? 9) - (breedRank[b.breed] ?? 9)));
  const locTotal = (g, key) => g.breeds.reduce((a, r) => a + (r.sizes?.[key] || 0), 0);

  return (
    <div data-testid="current-stock-view">
      {/* filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <MultiLocationFilter locations={locations} selected={fc} onChange={setFc} testid="current-loc" />
        <select value={breed} onChange={(e) => setBreed(e.target.value)} data-testid="current-breed" className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none">
          <option value="all">Tüm Çeşitler</option>
          {breeds.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <input type="date" max={TODAY} value={targetDate} onChange={(e) => setTargetDate(e.target.value || TODAY)} data-testid="current-date"
          className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none" />
        {isHistorical && (
          <button onClick={() => setTargetDate(TODAY)} data-testid="current-today-btn"
            className="h-10 px-4 rounded-lg border border-input bg-background text-sm font-semibold hover:bg-secondary transition-colors inline-flex items-center gap-1.5">
            <RotateCcw size={14} /> Bugüne Dön
          </button>
        )}
      </div>

      {isHistorical && (
        <div data-testid="historical-badge" className="mb-5 flex items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-2.5 text-sm text-sky-700 dark:text-sky-300">
          <Info size={16} className="shrink-0" />
          <span><strong>{new Date(targetDate).toLocaleDateString("tr-TR")}</strong> tarihi gün sonu stok durumu gösteriliyor</span>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={Package} tone="primary" label="Toplam Stok (Karton)" value={fmt(totalCartons)} testid="kpi-total" />
        <Kpi icon={ShieldCheck} tone="sky" label="Sağlam Ticari (Karton)" value={fmt(commercial)} testid="kpi-commercial" />
        <Kpi icon={AlertTriangle} tone="amber" label="Fire / İkincil (Karton)" value={fmt(fire)} testid="kpi-fire" />
        <Kpi icon={Clock} tone="muted" label="Son Güncelleme" value={lastUpdate ? new Date(lastUpdate).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-"} testid="kpi-updated" small />
      </div>

      {/* matrix */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="current-stock-matrix">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-left">
                <Th>Lokasyon / Depo</Th><Th>Tavuk Çeşidi</Th>
                {items.map((s) => <Th key={s.key} className="text-right">{s.label}</Th>)}
                <Th className="text-right">Toplam Krt.</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={items.length + 3} className="px-3 py-10 text-center text-muted-foreground">Stok kaydı bulunamadı.</td></tr>
              ) : (
                <>
                  {groups.map((g) => (
                    <Fragment key={g.name}>
                      <tr className="bg-primary/10" data-testid={`stock-loc-${g.name}`}>
                        <td colSpan={items.length + 3} className="px-3 py-2 font-head font-bold text-primary uppercase tracking-wide text-xs">{g.name} Kümesi</td>
                      </tr>
                      {g.breeds.map((r) => (
                        <tr key={r.breed} className="border-t border-border hover:bg-secondary/30" data-testid={`current-row-${r.coop_id}-${r.breed}`}>
                          <td className="px-3 py-2.5" />
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary">
                              <span className={`h-2 w-2 rounded-full ${BREED_DOT[r.breed] || "bg-slate-400"}`} />{r.breed_label}
                            </span>
                          </td>
                          {items.map((s) => {
                            const v = r.sizes?.[s.key] || 0;
                            return <td key={s.key} className={`px-3 py-2.5 text-right tabular-nums ${v <= 0 ? "text-muted-foreground/40" : "font-medium"}`}>{fmt(v)}</td>;
                          })}
                          <td className="px-3 py-2.5 text-right tabular-nums font-bold text-primary">{fmt(sum(allKeys, r))}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-border bg-secondary/50 font-semibold" data-testid={`stock-subtotal-${g.name}`}>
                        <td colSpan={2} className="px-3 py-2.5 text-primary">↳ {g.name} Kümesi Toplamı</td>
                        {items.map((s) => <td key={s.key} className="px-3 py-2.5 text-right tabular-nums">{fmt(locTotal(g, s.key))}</td>)}
                        <td className="px-3 py-2.5 text-right tabular-nums text-primary">{fmt(items.reduce((a, s) => a + locTotal(g, s.key), 0))}</td>
                      </tr>
                    </Fragment>
                  ))}
                  <tr className="border-t-2 border-primary/40 bg-primary/10 font-bold" data-testid="current-grand-total">
                    <td colSpan={2} className="px-3 py-3 uppercase tracking-wide">🏆 İşletme Genel Toplam</td>
                    {items.map((s) => <td key={s.key} className="px-3 py-3 text-right tabular-nums">{fmt(grand[s.key])}</td>)}
                    <td className="px-3 py-3 text-right tabular-nums text-primary">{fmt(totalCartons)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Günlük Çıkan Üretim (seçili günün üretim icmali) ---------------- */
function DailyProductionSummaryView() {
  const { meta, coops } = useApp();
  const { can } = useAuth();
  const locations = coops.filter((c) => (c.type === "location") || (c.type === "warehouse" && can("view_central_warehouse_stock")));
  const breeds = meta.breeds || [];
  const items = [...meta.egg_sizes, ...meta.defects];
  const eggKeys = new Set(meta.egg_sizes.map((s) => s.key));
  const [day, setDay] = useState(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  });
  const [fc, setFc] = useState([]);
  const [breed, setBreed] = useState("all");
  const [rows, setRows] = useState([]);
  const [prev, setPrev] = useState({ byKey: {}, byLoc: {}, grand: 0 });

  const load = useCallback(async () => {
    const parts = day.split("-").map(Number);
    const pd = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    pd.setUTCDate(pd.getUTCDate() - 1);
    const prevDay = pd.toISOString().slice(0, 10);
    const [{ data }, { data: pr }] = await Promise.all([
      api.get(`/production?start=${day}&end=${day}`),
      api.get(`/production?start=${prevDay}&end=${prevDay}`),
    ]);
    const allKeys = EGG_ORDER.concat(DEFECT_ORDER);
    const resolve = (r) => {
      const sc = coops.find((c) => c.id === r.coop_id);
      if (!sc) return null;
      return { locId: sc.parent_id || sc.id, br: sc.breed || "kirmizi", sc };
    };
    const agg = {};
    data.forEach((r) => {
      const m = resolve(r);
      if (!m) return;
      const key = `${m.locId}|${m.br}`;
      const row = agg[key] || (agg[key] = {
        coop_id: m.locId, location_name: coops.find((c) => c.id === m.locId)?.name || m.sc.description || "-",
        breed: m.br, breed_label: BREED_LABELS[m.br] || m.br, sizes: {},
      });
      items.forEach((it) => {
        const src = eggKeys.has(it.key) ? r.sizes : r.defects;
        row.sizes[it.key] = (row.sizes[it.key] || 0) + ((src || {})[it.key] || 0);
      });
    });
    let out = Object.values(agg).filter((r) => Object.values(r.sizes).some((v) => v !== 0));
    if (fc.length) out = out.filter((r) => fc.includes(r.coop_id));
    if (breed !== "all") out = out.filter((r) => r.breed === breed);
    setRows(out);
    // önceki gün toplam kartonları (kümes/ırk, lokasyon, genel)
    const byKey = {}, byLoc = {}; let grand = 0;
    pr.forEach((r) => {
      const m = resolve(r);
      if (!m) return;
      if (fc.length && !fc.includes(m.locId)) return;
      if (breed !== "all" && m.br !== breed) return;
      const t = allKeys.reduce((a, k) => a + ((eggKeys.has(k) ? r.sizes : r.defects)?.[k] || 0), 0);
      byKey[`${m.locId}|${m.br}`] = (byKey[`${m.locId}|${m.br}`] || 0) + t;
      byLoc[m.locId] = (byLoc[m.locId] || 0) + t;
      grand += t;
    });
    setPrev({ byKey, byLoc, grand });
  }, [day, fc, breed, coops]);
  useEffect(() => { load(); }, [load]);

  const allKeys = EGG_ORDER.concat(DEFECT_ORDER);
  const sum = (keys, r) => keys.reduce((a, k) => a + (r.sizes?.[k] || 0), 0);
  const totalCartons = rows.reduce((a, r) => a + sum(allKeys, r), 0);
  const totalAdet = rows.reduce((a, r) => a + items.reduce((s, it) => s + (r.sizes[it.key] || 0) * (MULTIPLIERS[it.key] || 0), 0), 0);
  const fire = rows.reduce((a, r) => a + sum(DEFECT_ORDER, r), 0);
  const breedTotal = (b) => rows.filter((r) => r.breed === b).reduce((a, r) => a + sum(allKeys, r), 0);
  const grand = {};
  items.forEach((s) => { grand[s.key] = rows.reduce((a, r) => a + (r.sizes?.[s.key] || 0), 0); });

  const byLoc = {};
  rows.forEach((r) => { (byLoc[r.coop_id] || (byLoc[r.coop_id] = { name: r.location_name, breeds: [] })).breeds.push(r); });
  const breedRank = { kirmizi: 0, tintin: 1, beyaz: 2 };
  const groups = Object.values(byLoc).sort((a, b) => a.name.localeCompare(b.name, "tr"));
  groups.forEach((g) => g.breeds.sort((a, b) => (breedRank[a.breed] ?? 9) - (breedRank[b.breed] ?? 9)));
  const locTotal = (g, key) => g.breeds.reduce((a, r) => a + (r.sizes?.[key] || 0), 0);

  return (
    <div data-testid="daily-production-view">
      {/* filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="daily-date" className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none" />
        <MultiLocationFilter locations={locations} selected={fc} onChange={setFc} testid="daily-loc" />
        <select value={breed} onChange={(e) => setBreed(e.target.value)} data-testid="daily-breed" className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none">
          <option value="all">Tüm Çeşitler</option>
          {breeds.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Kpi icon={Egg} tone="primary" label="Bugün Toplanan Üretim" value={fmt(totalCartons)} sub={`${fmt(totalAdet)} adet`} testid="daily-kpi-total" />
        <Kpi icon={Package} tone="red" label="Kırmızı Üretim (Krt)" value={fmt(breedTotal("kirmizi"))} testid="daily-kpi-kirmizi" />
        <Kpi icon={Package} tone="amber" label="Tintin Üretim (Krt)" value={fmt(breedTotal("tintin"))} testid="daily-kpi-tintin" />
        <Kpi icon={Package} tone="muted" label="Beyaz Üretim (Krt)" value={fmt(breedTotal("beyaz"))} testid="daily-kpi-beyaz" />
        <Kpi icon={AlertTriangle} tone="amber" label="Bugünkü Fire (Krt)" value={fmt(fire)} testid="daily-kpi-fire" />
      </div>

      {/* matrix */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="daily-production-matrix">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-left">
                <Th>Lokasyon / Kümes</Th><Th>Tavuk Çeşidi</Th>
                {items.map((s) => <Th key={s.key} className="text-right">{s.label}</Th>)}
                <Th className="text-right">Toplam Krt.</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={items.length + 3} className="px-3 py-10 text-center text-muted-foreground">Seçilen gün için üretim kaydı bulunamadı.</td></tr>
              ) : (
                <>
                  {groups.map((g) => (
                    <Fragment key={g.name}>
                      <tr className="bg-primary/10" data-testid={`daily-loc-${g.name}`}>
                        <td colSpan={items.length + 3} className="px-3 py-2 font-head font-bold text-primary uppercase tracking-wide text-xs">{g.name} Kümesi (O Günkü Üretim)</td>
                      </tr>
                      {g.breeds.map((r) => (
                        <tr key={r.breed} className="border-t border-border hover:bg-secondary/30" data-testid={`daily-row-${r.coop_id}-${r.breed}`}>
                          <td className="px-3 py-2.5" />
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-secondary">
                              <span className={`h-2 w-2 rounded-full ${BREED_DOT[r.breed] || "bg-slate-400"}`} />{r.breed_label}
                            </span>
                          </td>
                          {items.map((s) => {
                            const v = r.sizes?.[s.key] || 0;
                            return <td key={s.key} className={`px-3 py-2.5 text-right tabular-nums ${v <= 0 ? "text-muted-foreground/40" : "font-medium"}`}>{fmt(v)}</td>;
                          })}
                          <td className="px-3 py-2.5 text-right tabular-nums font-bold text-primary">{fmt(sum(allKeys, r))}<Delta cur={sum(allKeys, r)} prev={prev.byKey[`${r.coop_id}|${r.breed}`]} /></td>
                        </tr>
                      ))}
                      <tr className="border-t border-border bg-secondary/50 font-semibold" data-testid={`daily-subtotal-${g.name}`}>
                        <td colSpan={2} className="px-3 py-2.5 text-primary">📊 ↳ {g.name} Günlük Üretim Toplamı</td>
                        {items.map((s) => <td key={s.key} className="px-3 py-2.5 text-right tabular-nums">{fmt(locTotal(g, s.key))}</td>)}
                        <td className="px-3 py-2.5 text-right tabular-nums text-primary">{fmt(items.reduce((a, s) => a + locTotal(g, s.key), 0))}<Delta cur={items.reduce((a, s) => a + locTotal(g, s.key), 0)} prev={prev.byLoc[g.breeds[0]?.coop_id]} /></td>
                      </tr>
                    </Fragment>
                  ))}
                  <tr className="border-t-2 border-primary/40 bg-primary/10 font-bold" data-testid="daily-grand-total">
                    <td colSpan={2} className="px-3 py-3 uppercase tracking-wide">🏆 Günlük İşletme Genel Toplam Üretimi</td>
                    {items.map((s) => <td key={s.key} className="px-3 py-3 text-right tabular-nums">{fmt(grand[s.key])}</td>)}
                    <td className="px-3 py-3 text-right tabular-nums text-primary">{fmt(totalCartons)}<Delta cur={totalCartons} prev={prev.grand} /></td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const TONES = {
  primary: "bg-primary/10 text-primary",
  sky: "bg-sky-500/10 text-sky-500",
  amber: "bg-amber-500/10 text-amber-500",
  red: "bg-red-500/10 text-red-500",
  muted: "bg-secondary text-muted-foreground",
};
const BREED_DOT = { kirmizi: "bg-red-500", tintin: "bg-amber-500", beyaz: "bg-slate-300 border border-slate-400" };
const Delta = ({ cur, prev }) => {
  if (prev === undefined || prev === null) return <span className="block text-[10px] text-muted-foreground/70">dün: veri yok</span>;
  const diff = cur - prev;
  if (diff === 0) return <span className="block text-[10px] text-muted-foreground">▬ dün ile aynı</span>;
  const up = diff > 0;
  const pct = prev > 0 ? (Math.abs(diff) / prev) * 100 : 100;
  return (
    <span className={`block text-[10px] font-semibold ${up ? "text-green-600 dark:text-green-400" : "text-destructive"}`}>
      {up ? "▲" : "▼"} %{fmtDec(pct)} <span className="font-normal text-muted-foreground">({up ? "+" : "−"}{fmt(Math.abs(diff))})</span>
    </span>
  );
};
const Kpi = ({ icon: Icon, tone, label, value, testid, small, sub }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${TONES[tone]}`}><Icon size={18} /></div>
      <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground leading-tight">{label}</span>
    </div>
    <div className={`mt-3 font-head font-bold tabular-nums ${small ? "text-lg" : "text-2xl"}`}>{value}</div>
    {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);
const Th = ({ children, className = "" }) => <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap ${className}`}>{children}</th>;

/* Çoklu lokasyon seçici (checkbox açılır menü) — boş seçim = tümü */
function MultiLocationFilter({ locations, selected, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const label = selected.length === 0 ? "Tüm Lokasyonlar"
    : selected.length === 1 ? (locations.find((l) => l.id === selected[0])?.name || "1 lokasyon")
      : `${selected.length} lokasyon seçili`;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid={testid} className="h-10 min-w-[210px] rounded-lg border border-input bg-background px-3 text-sm text-left flex items-center justify-between gap-2 focus:ring-2 focus:ring-primary outline-none">
        <span className="truncate">{label}</span><ChevronDown size={15} className="opacity-60 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-60 max-h-72 overflow-auto rounded-lg border border-border bg-popover shadow-lg p-1" data-testid={`${testid}-menu`}>
          <button onClick={() => onChange([])} data-testid={`${testid}-all`} className={`w-full text-left px-2 py-1.5 rounded-md text-sm hover:bg-secondary ${selected.length === 0 ? "text-primary font-semibold" : ""}`}>Tüm Lokasyonlar</button>
          <div className="h-px bg-border my-1" />
          {locations.map((l) => (
            <label key={l.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-secondary cursor-pointer" data-testid={`${testid}-opt-${l.id}`}>
              <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggle(l.id)} className="accent-[hsl(var(--primary))] h-4 w-4" />
              <span className="truncate">{l.name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Öngörülen Stok (projeksiyon — mevcut mantık korunur) ---------------- */
const COLORS = ["#16a34a", "#f59e0b", "#0ea5e9", "#a855f7", "#ef4444", "#14b8a6", "#eab308", "#ec4899"];

function ProjectedStockView() {
  const { meta, coops } = useApp();
  const { can } = useAuth();
  const [basis, setBasis] = useState(7);
  const [days, setDays] = useState(7);
  const [fc, setFc] = useState([]);
  const [breed, setBreed] = useState("all");
  const [data, setData] = useState([]);
  const locations = coops.filter((c) => (c.type === "location") || (c.type === "warehouse" && can("view_central_warehouse_stock")));
  const breeds = meta.breeds || [];
  const items = [...meta.egg_sizes, ...meta.defects];

  const load = useCallback(async () => {
    const q2 = breed !== "all" ? `&breed=${breed}` : "";
    const { data } = await api.get(`/forecast?days=${days}&basis=${basis}${q2}`);
    setData(fc.length ? data.filter((c) => fc.includes(c.location_id)) : data);
  }, [fc, breed, days, basis]);
  useEffect(() => { load(); }, [load]);

  // Zincirleme matris: dönen (lokasyon,çeşit) girişleri gün gün toplanır
  const n = data[0]?.days?.length || 0;
  const combined = Array.from({ length: n }).map((_, i) => {
    const row = { date: data[0]?.days[i]?.date, status: data[0]?.days[i]?.status, sizes: {}, add: {}, total: 0, negative: false };
    items.forEach((s) => { row.sizes[s.key] = 0; row.add[s.key] = 0; });
    data.forEach((c) => {
      const d = c.days[i];
      if (!d) return;
      items.forEach((s) => {
        row.sizes[s.key] += d.sizes[s.key] || 0;
        row.add[s.key] += c.avg_daily?.[s.key] || 0;
      });
      row.total += d.total;
      if (d.negative) row.negative = true;
    });
    return row;
  });
  const chartData = combined.map((r) => ({ date: r.date, "Toplam Stok": r.total }));
  const dailyTotalAdd = items.reduce((a, s) => a + (combined[1]?.add[s.key] || 0), 0);

  const segBtn = (active) =>
    `px-3 h-8 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`;

  return (
    <div data-testid="projected-stock-view">
      {/* controls */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <MultiLocationFilter locations={locations} selected={fc} onChange={setFc} testid="forecast-coop" />
        <select value={breed} onChange={(e) => setBreed(e.target.value)} data-testid="forecast-breed" className="h-10 rounded-lg border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary outline-none">
          <option value="all">Tüm Çeşitler</option>
          {breeds.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
        </select>
        <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="forecast-basis-group">
          {[3, 5, 7].map((b) => (
            <button key={b} onClick={() => setBasis(b)} data-testid={`basis-${b}`} className={segBtn(basis === b)}>Son {b} Gün Ort.</button>
          ))}
        </div>
        <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="forecast-horizon-group">
          {[7, 15, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`horizon-${d}`} className={segBtn(days === d)}>{d} Gün</button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 mb-6 text-sm text-muted-foreground" data-testid="forecast-formula-note">
        Baz gün fiili stok üzerine, her cins için <span className="font-semibold text-foreground">son {basis} günün ortalama üretimi</span> (günlük ~<span className="font-semibold text-primary">{fmt(dailyTotalAdd)}</span> karton) her gün zincirleme eklenir; planlı sevkiyatlar ilgili günde düşülür.
      </div>

      {/* chart */}
      <div className="rounded-xl border border-border bg-card p-5 mb-6">
        <h3 className="font-head font-semibold mb-4">Toplam Stok Projeksiyonu (Zincirleme)</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={fmt} width={70} />
            <Tooltip formatter={fmt} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="Toplam Stok" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 30-day chained matrix */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="forecast-matrix">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/60 text-left">
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap sticky left-0 bg-secondary/60">Tarih</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">Gün</th>
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">Durum</th>
                {items.map((s) => <th key={s.key} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground text-right whitespace-nowrap">{s.label}</th>)}
                <th className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground text-right whitespace-nowrap">Toplam Karton</th>
              </tr>
            </thead>
            <tbody>
              {combined.length === 0 ? (
                <tr><td colSpan={items.length + 4} className="px-3 py-10 text-center text-muted-foreground">Projeksiyon verisi bulunamadı.</td></tr>
              ) : combined.map((r, i) => (
                <tr key={i} className={`border-t border-border ${r.status === "actual" ? "bg-primary/5" : "hover:bg-secondary/30"}`} data-testid={`forecast-row-${i}`}>
                  <td className={`px-3 py-2.5 whitespace-nowrap font-medium sticky left-0 ${r.status === "actual" ? "bg-primary/5" : "bg-card"}`}>{r.date}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground text-xs">{i === 0 ? "Başlangıç" : `+${i}. Gün`}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.status === "actual" ? "bg-green-500/15 text-green-600 dark:text-green-400" : "bg-sky-500/15 text-sky-600 dark:text-sky-400"}`}>
                      {r.status === "actual" ? "🟢 Fiili" : "🔵 Öngörülen"}
                    </span>
                  </td>
                  {items.map((s) => {
                    const v = r.sizes[s.key] || 0;
                    const add = r.add[s.key] || 0;
                    return (
                      <td key={s.key} className={`px-3 py-2.5 text-right tabular-nums ${r.negative && v <= 0 ? "bg-destructive/15 text-destructive font-semibold" : v <= 0 ? "text-muted-foreground/40" : ""}`}>
                        {fmt(v)}
                        {i > 0 && add > 0 && <span className="block text-[10px] text-primary/70">+{fmt(add)}</span>}
                      </td>
                    );
                  })}
                  <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${r.negative ? "text-destructive" : "text-primary"}`}>{fmt(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
